import 'babel-polyfill';
import 'cross-fetch/polyfill';
import 'es6-promise/auto';

import assert from 'http-assert-plus';
import Vue from 'vue';
import VueClipboard from 'vue-clipboard2';
import * as Sentry from '@sentry/vue';
import { captureLog } from '@src/lib/logs';
import { fetchLatestStatus, startFetchingLatestStatus } from '@src/lib/status';

import authMixin from '@/mixins/auth';
import createApp from '@/main';

// Allows clipboard to work within a bootstrap container
VueClipboard.config.autoSetContainer = true;
Vue.use(VueClipboard);

const isCypress = window.IS_CYPRESS_FBA0 === true || window.IS_CYPRESS_AC6F === true;
delete window.IS_CYPRESS_FBA0; delete window.IS_CYPRESS_AC6F;
const isNativeApp = window.IS_NATIVE_APP_BD93 === true || window.IS_NATIVE_APP_A481 === true;
delete window.IS_NATIVE_APP_BD93; delete window.IS_NATIVE_APP_A481;

Sentry.init({
  Vue,
  dsn: !isCypress ? process.env.SENTRY_DSN : null,
  release: process.env.COMMIT_SHA || undefined,
  debug: process.env.NODE_ENV !== 'production',
  environment: process.env.IS_DEV_HOSTNAME ? 'development' : process.env.NODE_ENV,
  defaultIntegrations: false,
});

Sentry.setTags({
  ...(isCypress ? { isCypress: 'true' } : {}),
  ...(isNativeApp ? { isNativeApp: 'true' } : {}),
  origin: window.location.origin,
  vueEnv: 'client',
});

if (process.env.NODE_ENV === 'production') {
  Vue.config.productionTip = process.env.IS_PROD_HOSTNAME === false;
  Vue.config.devtools = process.env.IS_PROD_HOSTNAME === false;
} else {
  Vue.config.productionTip = true;
  Vue.config.devtools = true;
  Vue.config.errorHandler = (err, vm, info) => Vue.$log.error(err, info);

  if (global && global.localStorage && global.sessionStorage) {
    const { localStorage, sessionStorage } = global;
    const key = 'rl:dev:proxy-api-url';
    const value = sessionStorage.getItem(key);
    const env = process.env.PROXY_API_URL;

    if ((env || value) && value !== env) {
      localStorage.clear();
      sessionStorage.clear();
      env ? sessionStorage.setItem(key, env) : sessionStorage.removeItem(key);

      if (process.env.PROXY_TOKEN) {
        localStorage.setItem('token', JSON.stringify(process.env.PROXY_TOKEN));
      }
    }
  }
}

const { app, router, apolloProvider } = createApp({
  data: {
    apiURL: '/api',
    hostname: window.location.hostname === 'localhost' ? window.location.host : window.location.hostname,
    isCypress,
    isDebug: `${window.location.search || ''}`.substr(1).split('&').includes('debug'),
    isNativeApp,
    origin: window.location.origin,
  },
});

if (process.env.ADMIN_UI) {
  router.onReady(() => app.$mount('#app'));
} else {
  /* eslint-disable-next-line no-inner-declarations */
  async function checkAuthentication(to, from, next) {
    try {
      const { methods: { resumeSession, getToken } } = authMixin;

      if (!`${process.env.PROXY_API_URL || window.location.origin}`.includes('.demo.reallinks.')) {
        if (process.env.NODE_ENV === 'production') {
          startFetchingLatestStatus();
        } else {
          fetchLatestStatus({ force: true });
          window.fetchLatestStatus = () => fetchLatestStatus();
        }
      }

      if (!app.isLoggedIn && getToken.call(app)) {
        // If we have an auth token, but the app isn't logged in, then this is the initial session restart
        await resumeSession.call(app).catch(err => app.$log.error(err));
      }

      const { isLoggedIn, isRecruiter, isRecruiterAdmin } = app;

      // If requiresAuth and not logged-in
      const requiresAuth = to.matched.some(r => r.meta && r.meta.requiresAuth);
      app.$log.debug({ to, requiresAuth, isLoggedIn });
      assert(!requiresAuth || isLoggedIn, 401, new Error('You must be logged-in to continue'), {
        code: 'AUTH_REQUIRED',
        redirect: {
          name: 'Login',
          query: { next: to.fullPath },
        },
      });

      // If requiresAdmin but is not a recruiter admin role, send to err page
      const requiresAdmin = to.matched.some(r => r.meta && r.meta.requiresAdmin);
      app.$log.debug({ to, requiresAdmin, isRecruiterAdmin });
      assert(!requiresAdmin || isRecruiterAdmin, 403, new Error('You must be an admin to continue'), {
        code: 'PERMISSION_DENIED',
      });

      // If requiresRecruiter but is not a recruiter role, send to err page
      const requiresRecruiter = to.matched.some(r => r.meta && r.meta.requiresRecruiter);
      app.$log.debug({ to, requiresRecruiter, isRecruiter });
      assert(!requiresRecruiter || isRecruiter, 403, new Error('You must be a recruiter to continue'), {
        code: 'PERMISSION_DENIED',
      });

      return next();
    } catch (err) {
      app.$log.error(err);
      if (err.redirect) {
        return next(err.redirect);
      } else {
        app.fullPageErr = err;
        return next();
      }
    }
  }

  router.beforeEach(checkAuthentication);

  router.onReady(() => checkAuthentication(router.currentRoute, { path: '/' }, replace => {
    if (replace) {
      router.replace(replace);
    }

    const { isLoggedIn } = app;
    if (isLoggedIn) {
      captureLog('SESSION_RESUMED', {
        user: { self: true },
      });
    }

    app.$mount('#app');
  }));
}

if (isCypress || `${window.location.origin}`.startsWith('http://localhost:') || process.env.NODE_ENV !== 'production') {
  window.Vue = Vue;
  window.$vueApp = app;
  window.$vueRouter = router;
  window.$vueApollo = apolloProvider;
}



// WEBPACK FOOTER //
// ./src/client-entry.js